// Admin: edit the public support contacts shown on the login / forgot-password screens.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { invalidateSupportContactsCache } from "@/components/SupportContactsCard";
import { HelpCircle, MessageCircle, Phone, Mail, Clock, Save, X, Loader2 } from "lucide-react";

export default function SupportContactsModal({ onClose }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/support-contacts").then(({ data }) => setForm({
      whatsapp: data.whatsapp || "",
      phone: data.phone || "",
      email: data.email || "",
      hours: data.hours || "",
      note: data.note || "",
    })).catch(() => setForm({ whatsapp:"", phone:"", email:"", hours:"", note:"" }));
  }, []);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const { data } = await api.patch("/admin/support-contacts", form);
      setForm({
        whatsapp: data.whatsapp || "",
        phone: data.phone || "",
        email: data.email || "",
        hours: data.hours || "",
        note: data.note || "",
      });
      invalidateSupportContactsCache();
      setMsg("تم حفظ أرقام الدعم — ستظهر للمستخدمين فوراً.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  if (!form) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="support-contacts-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl w-full max-w-md p-5 talabya-shadow-float animate-fade-up">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <HelpCircle size={18} className="text-primary-brand"/>
            <h3 className="font-black text-lg" style={{fontFamily:"Cairo"}}>أرقام الدعم</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[var(--surface-alt)] flex items-center justify-center" data-testid="support-close"><X size={16}/></button>
        </div>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          هذه الأرقام تظهر للمستخدمين في شاشة استعادة كلمة المرور. اترك الحقل فارغاً لإخفائه.
        </p>

        <Field icon={<MessageCircle size={14} className="text-green-600"/>} label="رقم واتساب" hint="يفتح واتساب مباشرة عند الضغط">
          <input dir="ltr" value={form.whatsapp} onChange={(e) => setForm({...form, whatsapp: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono"
            placeholder="+218911234567" data-testid="input-whatsapp"/>
        </Field>

        <Field icon={<Phone size={14} className="text-primary-brand"/>} label="رقم الهاتف / الاتصال" hint="اتصال هاتفي عادي">
          <input dir="ltr" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono"
            placeholder="+218921234567" data-testid="input-phone"/>
        </Field>

        <Field icon={<Mail size={14} className="text-blue-600"/>} label="البريد الإلكتروني">
          <input dir="ltr" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none"
            placeholder="support@talabya.ly" data-testid="input-email"/>
        </Field>

        <Field icon={<Clock size={14} className="text-blue-700"/>} label="ساعات الدعم">
          <input value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none"
            placeholder="9 صباحاً – 12 مساءً" data-testid="input-hours"/>
        </Field>

        <Field label="ملاحظة إضافية (اختياري)">
          <textarea value={form.note} onChange={(e) => setForm({...form, note: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none resize-none"
            rows={2}
            placeholder="مثال: يُفضّل التواصل عبر واتساب لسرعة الرد"
            data-testid="input-note"/>
        </Field>

        {msg && <div className="mt-2 text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg" data-testid="support-msg">{msg}</div>}
        {err && <div className="mt-2 text-sm bg-red-50 text-red-600 px-3 py-2 rounded-lg" data-testid="support-err">{err}</div>}

        <div className="mt-4 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] text-[var(--text-secondary)] font-bold">إلغاء</button>
          <button onClick={save} disabled={busy}
            className="flex-1 py-3 rounded-xl bg-primary-brand text-white font-black disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="support-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {busy ? "جاري..." : "حفظ"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="text-xs font-bold text-[var(--text-secondary)] mb-1 flex items-center gap-1.5">
        {icon}{label}
      </label>
      {children}
      {hint && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{hint}</div>}
    </div>
  );
}
